import { useMemo, useState } from "react";
import {
  Alert,
  Combobox,
  FormModal,
  Select,
  Text,
} from "@marekl/adminer/components";
import { notifySuccess } from "@marekl/adminer/api";
import {
  useAddPersonToTeam,
  type MembershipKind,
  type MembershipRole,
} from "../../../lib/queries/memberships";
import type { PersonListItem } from "../../../lib/queries/persons";
import { useTeams, type Team } from "../../../lib/queries/teams";
import { MEMBERSHIP_KIND_LABELS } from "../../../lib/memberState";
import {
  LEADER_ROLE_OPTIONS,
  REQUIRED_DATA_FIELDS,
  ROLE_OPTIONS,
} from "../../team/TeamDetailPage/memberFormSchema";
import { RoleCheckboxes } from "../../team/TeamDetailPage/RoleCheckboxes";
import { useMe, hasAnyGlobalRole } from "../../../auth/queries";
import { useSeason } from "../../../season";
import type { ID } from "../../../lib/id";

const REQUIRED_FIELD_LABELS: Record<(typeof REQUIRED_DATA_FIELDS)[number], string> = {
  street: "ulice a číslo popisné",
  city: "město",
  postal_code: "PSČ",
};

// Which kinds a roster may hold follows the TEAM's federation
// (membership.kindAllowedOn): a ČNSMS team holds člen / nečlen, a team outside
// ČNSMS holds zahraniční členy only.
function kindsFor(team: Team | undefined): MembershipKind[] {
  if (!team) return ["registered", "guest"];
  return team.federation_is_primary ? ["registered", "guest"] : ["foreign"];
}

// Mirrors rbac.CanAdministerTeam for the two roles that reach the registry:
// an admin manages every team, a members_admin the central ones (ČNSMS, the
// umbrella, a team under no federation). Offering a national federation's
// team to a members_admin would end in a 403.
function manageable(team: Team, isAdmin: boolean): boolean {
  return isAdmin || team.federation_is_primary || !!team.federation_is_umbrella || !team.federation_id;
}

/**
 * AddToTeamModal puts an existing person on a team's soupiska for the active
 * season, without creating a second Person: the members registry's way to
 * move somebody to another club. The person keeps their ČNSMS ID and history;
 * their rows on other teams are untouched.
 *
 * Only admin / members_admin reach the registry (ClenskaZakladnaRoute), and
 * the server allows a person with no tie to the team only to those roles.
 */
export function AddToTeamModal({
  person,
  open,
  onClose,
}: {
  person: PersonListItem;
  open: boolean;
  onClose: () => void;
}) {
  const { activeSeason } = useSeason();
  const { data: me } = useMe();
  const isAdmin = hasAnyGlobalRole(me, "admin");
  const teams = useTeams({ status: "active" }, { enabled: open });
  const add = useAddPersonToTeam();

  // No reset on open: the registry mounts this dialog per person and
  // unmounts it on close, so every opening starts from these defaults.
  const [teamId, setTeamId] = useState<ID | null>(null);
  const [kind, setKind] = useState<MembershipKind>("registered");
  const [roles, setRoles] = useState<MembershipRole[]>(["athlete"]);

  const options = useMemo(
    () =>
      (teams.data ?? [])
        .filter((t) => manageable(t, isAdmin))
        .sort((a, b) => a.name.localeCompare(b.name, "cs")),
    [teams.data, isAdmin],
  );
  const team = options.find((t) => t.id === teamId);
  const kinds = kindsFor(team);
  // The team's own leader: may not be Asistent and may hold no role at all
  // (validateRoles), the same rules the roster dialog applies.
  const isLeader = !!team && team.leader?.person.id === person.id;

  // A člen owes the address (validatePersonFull). The server would refuse
  // with a bare „Povinné pole" for a field this dialog does not show, so the
  // gap is named here, before the submit.
  const missing =
    kind === "registered"
      ? REQUIRED_DATA_FIELDS.filter((f) => !(person[f] ?? "").trim())
      : [];

  function pickTeam(id: ID | null) {
    setTeamId(id);
    const next = options.find((t) => t.id === id);
    const nextKinds = kindsFor(next);
    if (!nextKinds.includes(kind)) setKind(nextKinds[0]);
    if (next?.leader?.person.id === person.id) {
      setRoles((r) => r.filter((role) => role !== "assistant"));
    }
  }

  function onSubmit() {
    if (!teamId) return;
    add.mutate(
      { team_id: teamId, person_id: person.id, roles, kind },
      {
        onSuccess: () => {
          notifySuccess(
            `${person.first_name} ${person.last_name}: přidáno do týmu ${team?.name ?? ""}.`,
          );
          onClose();
        },
      },
    );
  }

  return (
    <FormModal
      open={open}
      title={`Přidat do týmu: ${person.first_name} ${person.last_name}`}
      submitLabel="Přidat do týmu"
      busy={add.isPending}
      canSubmit={teamId !== null && missing.length === 0 && (isLeader || roles.length > 0)}
      error={add.error}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      <Text as="p" variant="muted">
        {activeSeason ? `Zapíše osobu na soupisku sezóny ${activeSeason.code}. ` : ""}
        Zůstane jí stejný záznam i ČNSMS ID.
      </Text>
      {person.is_member && (
        // Adding is not moving: the old soupiska keeps the person until
        // somebody removes them there, and two registrations in one season
        // are not refused (yet). is_member is the active season's answer.
        // teams_label is deliberately not quoted: it is the newest season's
        // teams plus any team the person leads, so it can name a club they
        // only lead, or hold as a nečlen, as a registration.
        <Alert variant="warn">
          {`Osoba už je v sezóně ${activeSeason?.code ?? ""} registrovaná v jiném týmu. Ta registrace zůstane; při přestupu osobu ze staré soupisky odeberte.`}
        </Alert>
      )}
      <Combobox<Team, ID>
        label="Tým"
        placeholder="Vyberte tým"
        options={options}
        loading={teams.isLoading}
        getOptionLabel={(t) => t.name}
        getOptionValue={(t) => t.id}
        getOptionKey={(t) => t.id}
        value={teamId}
        onChange={pickTeam}
      />
      <Select
        label="Typ členství"
        value={kind}
        disabled={kinds.length < 2}
        onChange={(e) => setKind(e.target.value as MembershipKind)}
        hint={kind === "registered" ? "Tým musí být do sezóny registrovaný." : undefined}
      >
        {kinds.map((k) => (
          <option key={k} value={k}>
            {MEMBERSHIP_KIND_LABELS[k]}
          </option>
        ))}
      </Select>
      {missing.length > 0 && (
        <Alert variant="err">
          {`Člen musí mít vyplněnou adresu. Osobě chybí: ${missing
            .map((f) => REQUIRED_FIELD_LABELS[f])
            .join(", ")}. Doplňte je přes „Upravit“.`}
        </Alert>
      )}
      <RoleCheckboxes
        value={roles}
        onChange={setRoles}
        options={isLeader ? LEADER_ROLE_OPTIONS : ROLE_OPTIONS}
        showLeaderNote={isLeader}
      />
    </FormModal>
  );
}
